import type { RentalCase, RentalCaseStatus } from '~/api/types/rentalCase'

export type CaseProcessStatus =
  | 'ACTIVE'
  | 'WAITING_APPRAISAL'
  | 'WAITING_INVOICE'
  | 'WAITING_TO_SEND'
  | 'SENT_TO_INSURANCE'
  | 'CREDIT_NOTE'
  | 'COMPLAINT'
  | 'ENDED'

export const caseProcessStatusOptions: { title: string; value: CaseProcessStatus }[] = [
  { title: 'Aktivní', value: 'ACTIVE' },
  { title: 'Čeká na odhad', value: 'WAITING_APPRAISAL' },
  { title: 'Čeká na fakturaci', value: 'WAITING_INVOICE' },
  { title: 'Čeká na odeslání na pojišťovnu', value: 'WAITING_TO_SEND' },
  { title: 'Odesláno na pojišťovnu', value: 'SENT_TO_INSURANCE' },
  { title: 'Dobropis', value: 'CREDIT_NOTE' },
  { title: 'Reklamace', value: 'COMPLAINT' },
  { title: 'Ukončeno', value: 'ENDED' }
]

export const caseProcessStatusLabels: Record<CaseProcessStatus, string> = {
  ACTIVE: 'Aktivní',
  WAITING_APPRAISAL: 'Čeká na odhad',
  WAITING_INVOICE: 'Čeká na fakturaci',
  WAITING_TO_SEND: 'Čeká na odeslání na pojišťovnu',
  SENT_TO_INSURANCE: 'Odesláno na pojišťovnu',
  CREDIT_NOTE: 'Dobropis',
  COMPLAINT: 'Reklamace',
  ENDED: 'Ukončeno'
}

export const caseProcessStatusColors: Record<CaseProcessStatus, string> = {
  ACTIVE: 'success',
  WAITING_APPRAISAL: 'warning',
  WAITING_INVOICE: 'purple',
  WAITING_TO_SEND: 'orange-darken-2',
  SENT_TO_INSURANCE: 'info',
  CREDIT_NOTE: 'teal',
  COMPLAINT: 'deep-purple',
  ENDED: 'grey'
}

export const caseProcessStatusPriority: Record<CaseProcessStatus, number> = {
  ACTIVE: 0,
  WAITING_APPRAISAL: 1,
  WAITING_INVOICE: 2,
  WAITING_TO_SEND: 3,
  SENT_TO_INSURANCE: 4,
  CREDIT_NOTE: 5,
  COMPLAINT: 6,
  ENDED: 7
}

export const caseProcessStatusNextValid: Record<CaseProcessStatus, CaseProcessStatus[]> = {
  ACTIVE: ['WAITING_APPRAISAL'],
  WAITING_APPRAISAL: ['WAITING_INVOICE'],
  WAITING_INVOICE: ['WAITING_TO_SEND'],
  WAITING_TO_SEND: ['SENT_TO_INSURANCE'],
  SENT_TO_INSURANCE: ['CREDIT_NOTE', 'ENDED'],
  CREDIT_NOTE: ['ENDED'],
  COMPLAINT: [],
  ENDED: []
}

export function toBEStatus(ps: CaseProcessStatus): RentalCaseStatus {
  switch (ps) {
    case 'ENDED': return 'ENDED'
    default: return 'ACTIVE'
  }
}

export function resolveProcessStatus(rc: Pick<RentalCase, 'customData'>): CaseProcessStatus {
  const ps = (rc.customData as { processStatus?: CaseProcessStatus })?.processStatus
  if (ps && ps in caseProcessStatusLabels) return ps
  return 'ACTIVE'
}

export const SELF_PAYER_INSURANCE_OPTIONS = [
  'Samoplátce',
  'Allianz s připojištěním samoplátce'
] as const

export type SelfPayerInsurance = typeof SELF_PAYER_INSURANCE_OPTIONS[number]

export const isSelfPayerInsurance = (insuranceCompany: string | null | undefined): boolean =>
  !!insuranceCompany && (SELF_PAYER_INSURANCE_OPTIONS as readonly string[]).includes(insuranceCompany)
